// Version: 1.13
// v1.13: The title and subtitle read on the grey page (they were near-white, so Tickets had no visible
//        title), and on a phone the header wraps: the buttons move under the text instead of squeezing
//        the subtitle into a one-word-wide column.
// v1.12: Raised by can be set by staff: a Raised by field on New Ticket (blank means you) and a Raised by box
//        with Save in the ticket drawer, suggesting the client's users and STG staff (app.py v4.40). Assign to on
//        New Ticket now sticks (the API used to drop it). Edit sends a cleared assignee as unassigned. The drawer's
//        10-second refresh no longer briefly shows the old values when it started just before a change.
// v1.11: Attachment links use the shared clickable-card class.
// v1.10: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size. Panels still drawn dark carry className="surface-dark" so their buttons pick up dark values.
// 1.9: Tickets show their category (e.g. Website issue, opened automatically
//      from website analytics by app.py v4.01).
// 1.8: Board view for staff (List / Board toggle, remembered per browser).
//      Columns run left to right: Unassigned (Open with no assignee), Open
//      (assigned), In Progress, On Hold, Resolved, and Closed behind a
//      toggle. Uses the shared Board.js (drag and drop on desktop, stacked
//      sections with a Move to dropdown on mobile). Moving an unassigned
//      ticket anywhere except Unassigned opens an assignee prompt first, then
//      sends status and assigned_to in one PUT. Dropping into Unassigned sets
//      the status to Open and clears the assignee. No backend change.
// 1.7: Ticket deletion (soft, matching app.py v2.80). Admin-only trash
//      icon in TicketDrawer header, confirm dialog, DELETE /api/tickets/id.
//      New "Recently Deleted" button (admin-only) opens DeletedTicketsPanel
//      — lists soft-deleted tickets with a Restore button per row, reads
//      retention_days from the API response rather than hardcoding the
//      90-day number so it can't silently drift from the backend's actual
//      TICKET_DELETE_RETENTION_DAYS setting. handleDeleteTicket/
//      handleRestore both useCallback-memoized, same reasoning as
//      handleUpdate in 1.6 — keeps their references stable so they don't
//      interfere with the drawer's 10s poll interval.
// 1.6: TicketDrawer now silently polls its own ticket every 10s while open
//      (previously only the list polled every 30s — an open drawer stayed
//      frozen at whatever data it had when opened). handleUpdate is now
//      useCallback-memoized so its reference stays stable across the
//      list's own 30s re-renders — without that, the drawer's 10s interval
//      would get torn down and reset every time the list polled, meaning
//      it could effectively never fire. Also tightened the desktop table's
//      fixed column widths at a new isCompact (<=1440px) tier — reusing
//      the existing useIsMobile(breakpoint) hook at a laptop-width
//      threshold rather than the 768px mobile one — since the previous
//      700px of fixed columns left little room for Title on a laptop
//      screen even though it technically fit within the 1300px container.
// 1.5: Attachments widened beyond images to PDF/Word/Excel (app.py v2.78+,
//      email-poller.py v1.3+). New AttachmentChip component renders images
//      as thumbnails (unchanged) and documents as a type-badged file chip
//      (name + size, opens in new tab) — content_type decides which.
// 1.4: TicketDrawer now renders t.attachments (images from inbound email,
//      persisted by app.py v2.77+) as thumbnails between Description and
//      Conversation. Only rendered when the array is non-empty — no
//      placeholder section for tickets without attachments.
// 1.3: Fixed CSRF token retrieval. auth.js stores the token in
//      localStorage under 'stg_csrf' on login; this file was previously
//      reading from window.__csrfToken (never assigned anywhere) and a
//      document.cookie 'csrf_token' cookie that does not exist — the
//      backend returns the token in the login JSON body, not a cookie.
//      Every ticket create/update/comment call was silently missing its
//      X-CSRF-Token header as a result. Both TicketModal.handleSave and
//      TicketDrawer.csrfHeader now read from localStorage 'stg_csrf'.
import { useState, useEffect, useCallback, useRef } from "react";
import Board from "./Board";

const API = "";

// v1.12: suggestions for Raised by: the chosen client's users first, then STG staff.
function RaisedBySuggestions({ id, people, slug }) {
  const list = (people || []).filter(p => !p.blocked && (
    (p.role === "client" && p.client_slug === slug) || p.role === "admin" || p.role === "manager" || p.role === "staff"));
  list.sort((a, b) => (a.role === "client" ? 0 : 1) - (b.role === "client" ? 0 : 1));
  return (
    <datalist id={id}>
      {list.map(p => (
        <option key={p.username} value={p.email || p.username}>
          {(p.name ? p.name + " · " : "") + (p.role === "client" ? "client" : "STG")}
        </option>
      ))}
    </datalist>
  );
}

// v1.9: ticket category tag (analytics opens "Website issue" tickets, app.py v4.01)
function CategoryTag({ c }) {
  return <span style={{ fontSize: 10, fontWeight: 700, color: "#0a84ff", background: "rgba(10,132,255,0.10)", border: "1px solid rgba(10,132,255,0.3)", borderRadius: 4, padding: "1px 6px", marginLeft: 6, whiteSpace: "nowrap" }}>{c}</span>;
}

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const PRIORITY_META = {
  Critical: { color: "#ff2d55", bg: "rgba(255,45,85,0.12)", dot: "#ff2d55" },
  High:     { color: "#ff9f0a", bg: "rgba(255,159,10,0.12)", dot: "#ff9f0a" },
  Medium:   { color: "#00d4ff", bg: "rgba(0,212,255,0.10)", dot: "#00d4ff" },
  Low:      { color: "#86868b", bg: "rgba(134,134,139,0.12)", dot: "#86868b" },
};

const STATUS_META = {
  "Open":        { color: "#ff9f0a", bg: "rgba(255,159,10,0.10)" },
  "In Progress": { color: "#00d4ff", bg: "rgba(0,212,255,0.10)" },
  "On Hold":     { color: "#bf5af2", bg: "rgba(191,90,242,0.10)" },
  "Resolved":    { color: "#30d158", bg: "rgba(48,209,88,0.10)" },
  "Closed":      { color: "#86868b", bg: "rgba(134,134,139,0.10)" },
};

const TYPE_ICONS = {
  Incident: "⚡",
  Request:  "",
  Change:   "",
};

function ago(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
}

function Badge({ label, meta }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 8px", borderRadius: 20,
      background: meta?.bg || "rgba(134,134,139,0.1)",
      color: meta?.color || "#86868b",
      fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
      whiteSpace: "nowrap",
    }}>
      {meta?.dot && (
        <span style={{
          width: 5, height: 5, borderRadius: "50%",
          background: meta.dot, flexShrink: 0
        }} />
      )}
      {label}
    </span>
  );
}

const DOC_KIND_META = {
  "application/pdf": { label: "PDF", color: "#ff2d55" },
  "application/msword": { label: "DOC", color: "#00d4ff" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": { label: "DOCX", color: "#00d4ff" },
  "application/vnd.ms-excel": { label: "XLS", color: "#30d158" },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": { label: "XLSX", color: "#30d158" },
};

function formatBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentChip({ a }) {
  const isImage = (a.content_type || "").startsWith("image/");
  const name = a.original_filename || a.filename;

  if (isImage) {
    return (
      <a
        href={a.url}
        target="_blank"
        rel="noopener noreferrer"
        title={name}
        style={{
          display: "block", width: 96, height: 96,
          borderRadius: 8, overflow: "hidden",
          border: "1px solid #3a3a3c", background: "#1c1c1e",
          flexShrink: 0,
        }}
      >
        <img
          src={a.url}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </a>
    );
  }

  const kind = DOC_KIND_META[a.content_type] || { label: "FILE", color: "#86868b" };
  return (
    <a
      href={a.url}
      target="_blank"
      rel="noopener noreferrer"
      title={name}
      className="btn-card"
      style={{
        display: "flex", alignItems: "center", gap: 10,
        textDecoration: "none", maxWidth: 240, flexShrink: 0,
      }}
    >
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
        padding: "4px 7px", borderRadius: 5, flexShrink: 0,
        color: kind.color, background: `${kind.color}1f`,
      }}>
        {kind.label}
      </span>
      <span style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "#f5f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name}
        </div>
        {a.size ? (
          <div style={{ fontSize: 10, color: "#86868b", marginTop: 1 }}>{formatBytes(a.size)}</div>
        ) : null}
      </span>
    </a>
  );
}

function StatCard({ label, value, color, sub }) {
  return (
    <div className="surface-dark" style={{
      background: "#1c1c1e", border: "1px solid #2c2c2e",
      borderRadius: 12, padding: "18px 20px",
      display: "flex", flexDirection: "column", gap: 4,
    }}>
      <div style={{ fontSize: 11, color: "#86868b", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || "#f5f5f7", lineHeight: 1.1 }}>
        {value ?? "—"}
      </div>
      {sub && <div style={{ fontSize: 11, color: "#86868b" }}>{sub}</div>}
    </div>
  );
}

// ── Create / Edit Ticket Modal ────────────────────────────────────────────────

function TicketModal({ onClose, onSave, clients, staff, role, userSlug, editTicket, people, currentUser }) {
  const isEdit = !!editTicket;
  const [form, setForm] = useState(editTicket ? {
    type:        editTicket.type,
    priority:    editTicket.priority,
    status:      editTicket.status,
    title:       editTicket.title,
    description: editTicket.description,
    client_slug: editTicket.client_slug,
    assigned_to: editTicket.assigned_to || "",
    raised_by:   editTicket.raised_by || "",
  } : {
    type: "Incident", priority: "Medium", status: "Open",
    title: "", description: "",
    client_slug: role === "client" ? userSlug : "",
    assigned_to: "",
    raised_by:   "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canEditClientFields = role !== "client";
  const canEditStatus       = role !== "client";
  const availableTypes      = role === "client"
    ? ["Incident", "Request"]
    : ["Incident", "Request", "Change"];

  async function handleSave() {
    if (!form.title.trim()) { setError("Title is required"); return; }
    if (!form.client_slug)  { setError("Client is required"); return; }
    setSaving(true); setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      const csrfStored = localStorage.getItem('stg_csrf');
      if (csrfStored) headers["X-CSRF-Token"] = csrfStored;

      let url = `${API}/api/tickets`;
      let method = "POST";
      if (isEdit) { url = `${API}/api/tickets/${editTicket.id}`; method = "PUT"; }

      const payload = { ...form };
      // v1.12: on edit a cleared assignee means unassigned; on create blank means none.
      if (!payload.assigned_to) {
        if (isEdit) payload.assigned_to = null; else delete payload.assigned_to;
      }
      payload.raised_by = (payload.raised_by || "").trim();
      if (!canEditClientFields || !payload.raised_by) delete payload.raised_by;

      const res = await fetch(url, { method, headers, credentials: "include", body: JSON.stringify(payload) });
      if (!res.ok) { const d = await res.json(); setError(d.error || "Failed"); setSaving(false); return; }
      const ticket = await res.json();
      onSave(ticket);
    } catch (e) {
      setError("Network error"); setSaving(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, backdropFilter: "blur(4px)",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="surface-dark" style={{
        background: "#1c1c1e", borderRadius: 16, width: "100%", maxWidth: 540,
        border: "1px solid #2c2c2e", overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#f5f5f7" }}>
            {isEdit ? `Edit ${editTicket.ref}` : "New Ticket"}
          </h2>
          <button className="btn btn-quiet btn-sm btn-icon" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          {error && (
            <div style={{ background: "rgba(255,45,85,0.1)", border: "1px solid rgba(255,45,85,0.3)", borderRadius: 8, padding: "10px 14px", color: "#ff2d55", fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label style={labelStyle}>Title</label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Brief description of the issue or request"
              style={inputStyle}
              maxLength={200}
            />
          </div>

          {/* Row: Type + Priority */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inputStyle}>
                {availableTypes.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={inputStyle}>
                {["Low","Medium","High","Critical"].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Row: Status + Client */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {canEditStatus && (
              <div>
                <label style={labelStyle}>Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                  {["Open","In Progress","On Hold","Resolved","Closed"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            )}
            {canEditClientFields ? (
              <div>
                <label style={labelStyle}>Client</label>
                <select value={form.client_slug} onChange={e => setForm(f => ({ ...f, client_slug: e.target.value }))} style={inputStyle}>
                  <option value="">— select —</option>
                  {clients.map(c => <option key={c.slug} value={c.slug}>{c.display_name}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label style={labelStyle}>Client</label>
                <div style={{ ...inputStyle, color: "#86868b", display: "flex", alignItems: "center" }}>
                  {userSlug?.replace(/_/g, " ")}
                </div>
              </div>
            )}
          </div>

          {/* Raised by — staff only (v1.12) */}
          {canEditClientFields && (
            <div>
              <label style={labelStyle}>Raised by</label>
              <input
                value={form.raised_by}
                onChange={e => setForm(f => ({ ...f, raised_by: e.target.value }))}
                placeholder={isEdit ? "" : `You (${currentUser})`}
                list="ticket-modal-raised-by"
                style={inputStyle}
                maxLength={200}
              />
              <RaisedBySuggestions id="ticket-modal-raised-by" people={people} slug={form.client_slug} />
            </div>
          )}

          {/* Assigned to — staff only */}
          {canEditClientFields && (
            <div>
              <label style={labelStyle}>Assign to</label>
              <select value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} style={inputStyle}>
                <option value="">— unassigned —</option>
                {staff.map(s => (
                  <option key={s.username} value={s.username}>
                    {s.email} {s.role === "manager" ? "(manager)" : "(admin)"}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Provide as much detail as possible…"
              rows={4}
              style={{ ...inputStyle, resize: "vertical", minHeight: 90, fontFamily: "inherit" }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 24px 20px",
          display: "flex", justifyContent: "flex-end", gap: 10,
          borderTop: "1px solid #2c2c2e",
        }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Recently Deleted Panel ───────────────────────────────────────────────────

function DeletedTicketsPanel({ onClose, onRestore }) {
  const [items, setItems]     = useState([]);
  const [retentionDays, setRetentionDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  const csrfHeader = () => {
    const token = localStorage.getItem('stg_csrf');
    return token ? { "X-CSRF-Token": token } : {};
  };

  useEffect(() => {
    fetch(`${API}/api/tickets/deleted`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { tickets: [], retention_days: 90 })
      .then(d => {
        setItems(d.tickets || []);
        if (d.retention_days) setRetentionDays(d.retention_days);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  async function restore(t) {
    setRestoringId(t.id);
    try {
      const res = await fetch(`${API}/api/tickets/${t.id}/restore`, {
        method: "POST", credentials: "include",
        headers: { ...csrfHeader() },
      });
      if (res.ok) {
        const restored = await res.json();
        setItems(prev => prev.filter(x => x.id !== t.id));
        onRestore(restored);
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || "Failed to restore ticket");
      }
    } finally { setRestoringId(null); }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, backdropFilter: "blur(4px)",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="surface-dark" style={{
        background: "#1c1c1e", borderRadius: 16, width: "100%", maxWidth: 560,
        border: "1px solid #2c2c2e", overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        maxHeight: "80vh", display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #2c2c2e", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f5f5f7" }}>Recently Deleted</h2>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#86868b" }}>Kept for {retentionDays} days, then permanently removed</p>
          </div>
          <button className="btn btn-secondary btn-icon" style={{ flexShrink: 0 }} onClick={onClose}>×</button>
        </div>

        <div style={{ overflow: "auto", padding: loading || items.length === 0 ? "40px 24px" : "10px" }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "#86868b", fontSize: 13 }}><span className="spinner" /></div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", color: "#86868b", fontSize: 13 }}>Nothing deleted recently</div>
          ) : (
            items.map(t => (
              <div key={t.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                padding: "12px 14px", borderRadius: 10,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontFamily: "'SF Mono',monospace", fontSize: 11, color: "#00d4ff", fontWeight: 600 }}>{t.ref}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#f5f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#86868b" }}>
                    Deleted {ago(t.deleted_at)}{t.deleted_by ? ` by ${t.deleted_by}` : ""}
                  </div>
                </div>
                <button className="btn btn-secondary" style={{ flexShrink: 0, opacity: restoringId === t.id ? 0.5 : 1 }} onClick={() => restore(t)} disabled={restoringId === t.id}>
                  {restoringId === t.id ? "Restoring…" : "Restore"}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Ticket Detail Drawer ──────────────────────────────────────────────────────

function TicketDrawer({ ticket, onClose, onUpdate, onDelete, staff, role, currentUser, people }) {
  const [t, setT] = useState(ticket);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const commentsRef = useRef(null);
  const isStaff = role === "admin" || role === "manager";
  const lastPatchAt = useRef(0);            // v1.12: drop refreshes that started before a change
  const [raisedBy, setRaisedBy] = useState(ticket.raised_by || "");
  const [raisedErr, setRaisedErr] = useState("");
  useEffect(() => { setRaisedBy(t.raised_by || ""); }, [t.raised_by]);

  const csrfHeader = () => {
    const token = localStorage.getItem('stg_csrf');
    return token ? { "X-CSRF-Token": token } : {};
  };

  async function handleDelete() {
    if (!window.confirm(`Delete ticket ${t.ref}? It'll be kept for 90 days in case you need to restore it, then permanently removed.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/tickets/${t.id}`, {
        method: "DELETE", credentials: "include",
        headers: { ...csrfHeader() },
      });
      if (res.ok) {
        onDelete(t.id);
        onClose();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || "Failed to delete ticket");
      }
    } finally { setDeleting(false); }
  }

  async function patch(payload) {
    setUpdating(true);
    lastPatchAt.current = Date.now();
    try {
      const res = await fetch(`${API}/api/tickets/${t.id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeader() },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const updated = await res.json();
        lastPatchAt.current = Date.now();
        setT(updated);
        onUpdate(updated);
        return true;
      }
      const d = await res.json().catch(() => ({}));
      alert(d.error || "Couldn't save the change");
      return false;
    } finally { setUpdating(false); }
  }

  async function saveRaisedBy() {
    const v = raisedBy.trim();
    if (!v) { setRaisedErr("Enter who raised it"); return; }
    setRaisedErr("");
    await patch({ raised_by: v });
  }

  async function postComment() {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const res = await fetch(`${API}/api/tickets/${t.id}/comments`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeader() },
        body: JSON.stringify({ body: comment, internal }),
      });
      if (res.ok) {
        const c = await res.json();
        const updated = { ...t, comments: [...(t.comments || []), c], updated_at: new Date().toISOString() };
        setT(updated);
        onUpdate(updated);
        setComment("");
        setTimeout(() => commentsRef.current?.scrollTo({ top: 9999, behavior: "smooth" }), 100);
      }
    } finally { setPosting(false); }
  }

  const visibleComments = (t.comments || []).filter(c => isStaff || !c.internal);

  // Silent refresh while the drawer is open — keeps status/comments/etc in
  // sync if someone else updates the ticket while you're viewing it. Same
  // "no spinner, no flash" approach as the 30s list poll, just faster
  // since a single open ticket is more likely to be actively worked on.
  useEffect(() => {
    const id = setInterval(async () => {
      const started = Date.now();
      try {
        const res = await fetch(`${API}/api/tickets/${ticket.id}`, { credentials: "include" });
        if (res.ok) {
          const fresh = await res.json();
          if (lastPatchAt.current >= started) return;   // a change landed meanwhile; this copy is stale
          setT(fresh);
          onUpdate(fresh);
        }
      } catch {}
    }, 10000);
    return () => clearInterval(id);
  }, [ticket.id, onUpdate]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 900,
      display: "flex", alignItems: "stretch",
    }}>
      {/* Backdrop */}
      <div style={{ flex: 1, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)" }} onClick={onClose} />

      {/* Drawer */}
      <div style={{
        width: "min(600px, 95vw)", background: "#111", borderLeft: "1px solid #2c2c2e",
        display: "flex", flexDirection: "column", overflow: "hidden",
        animation: "slideIn 0.2s ease",
      }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Drawer header */}
        <div style={{
          padding: "20px 24px", borderBottom: "1px solid #2c2c2e",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ fontFamily: "'SF Mono',monospace", fontSize: 11, color: "#00d4ff", fontWeight: 600 }}>
                {t.ref}
              </span>
              <span style={{ fontSize: 13, color: "#86868b" }}>{TYPE_ICONS[t.type]} {t.type}</span>{t.category && <CategoryTag c={t.category} />}
              <Badge label={t.priority} meta={PRIORITY_META[t.priority]} />
              <Badge label={t.status} meta={STATUS_META[t.status]} />
            </div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f5f5f7", lineHeight: 1.3 }}>
              {t.title}
            </h2>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexShrink: 0 }}>
            {role === "admin" && (
              <button className="btn btn-danger btn-icon" style={{ opacity: deleting ? 0.5 : 1 }} onClick={handleDelete} disabled={deleting} title="Delete ticket">🗑</button>
            )}
            <button className="btn btn-secondary btn-icon" style={{ flexShrink: 0 }} onClick={onClose}>×</button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>

          {/* Meta grid */}
          <div className="surface-dark" style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1,
            borderBottom: "1px solid #2c2c2e", background: "#1c1c1e",
          }}>
            {[
              ["Client", t.client_name],
              ["Raised by", t.raised_by],
              ["Created", fmt(t.created_at)],
              ["Updated", fmt(t.updated_at)],
              ["Source", t.source === "email" ? " Email" : " Portal"],
              isStaff ? ["Assigned to", t.assigned_to || "—"] : ["Status", t.status],
            ].map(([k, v]) => (
              <div key={k} style={{ padding: "12px 16px", borderBottom: "1px solid #2c2c2e" }}>
                <div style={{ fontSize: 10, color: "#86868b", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>{k}</div>
                <div style={{ fontSize: 13, color: "#f5f5f7", wordBreak: "break-all" }}>{v || "—"}</div>
              </div>
            ))}
          </div>

          {/* Staff inline controls */}
          {isStaff && (
            <div className="surface-dark" style={{
              padding: "14px 20px", borderBottom: "1px solid #2c2c2e",
              display: "flex", gap: 10, flexWrap: "wrap", background: "#161616",
            }}>
              <select
                value={t.status}
                onChange={e => patch({ status: e.target.value })}
                disabled={updating}
                style={{ ...inlineSelect, color: STATUS_META[t.status]?.color }}
              >
                {["Open","In Progress","On Hold","Resolved","Closed"].map(s => <option key={s}>{s}</option>)}
              </select>
              <select
                value={t.priority}
                onChange={e => patch({ priority: e.target.value })}
                disabled={updating}
                style={{ ...inlineSelect, color: PRIORITY_META[t.priority]?.color }}
              >
                {["Low","Medium","High","Critical"].map(p => <option key={p}>{p}</option>)}
              </select>
              <select
                value={t.assigned_to || ""}
                onChange={e => patch({ assigned_to: e.target.value || null })}
                disabled={updating}
                style={inlineSelect}
              >
                <option value="">— unassigned —</option>
                {staff.map(s => <option key={s.username} value={s.username}>{s.email}</option>)}
              </select>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  value={raisedBy}
                  onChange={e => setRaisedBy(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") saveRaisedBy(); }}
                  list={`raised-by-${t.id}`}
                  placeholder="Raised by"
                  title="Raised by"
                  aria-label="Raised by"
                  maxLength={200}
                  disabled={updating}
                  style={{ ...inlineSelect, cursor: "text", minWidth: 200 }}
                />
                <RaisedBySuggestions id={`raised-by-${t.id}`} people={people} slug={t.client_slug} />
                {raisedBy.trim() !== (t.raised_by || "") && (
                  <button className="btn btn-primary btn-sm" onClick={saveRaisedBy} disabled={updating}>Save</button>
                )}
                {raisedErr && <span style={{ fontSize: 11, color: "#ff2d55" }}>{raisedErr}</span>}
              </div>
            </div>
          )}

          {/* Description */}
          {t.description && (
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #2c2c2e" }}>
              <div style={{ fontSize: 11, color: "#86868b", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Description</div>
              <div style={{ fontSize: 13, color: "#d1d1d6", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                {t.description}
              </div>
            </div>
          )}

          {/* Attachments */}
          {t.attachments && t.attachments.length > 0 && (
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #2c2c2e" }}>
              <div style={{ fontSize: 11, color: "#86868b", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>
                Attachments ({t.attachments.length})
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {t.attachments.map(a => <AttachmentChip key={a.filename} a={a} />)}
              </div>
            </div>
          )}

          {/* Comments */}
          <div ref={commentsRef} style={{ flex: 1, overflow: "auto", padding: "16px 20px" }}>
            <div style={{ fontSize: 11, color: "#86868b", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>
              Conversation {visibleComments.length > 0 && `(${visibleComments.length})`}
            </div>
            {visibleComments.length === 0 && (
              <div style={{ color: "#86868b", fontSize: 13, fontStyle: "italic" }}>No messages yet.</div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {visibleComments.map(c => {
                const isOwn = c.author === currentUser;
                const isInt = c.internal;
                return (
                  <div key={c.id} style={{
                    display: "flex", flexDirection: "column",
                    alignItems: isOwn ? "flex-end" : "flex-start",
                  }}>
                    <div style={{
                      maxWidth: "85%",
                      background: isInt
                        ? "rgba(191,90,242,0.08)"
                        : isOwn
                          ? "rgba(0,212,255,0.1)"
                          : "#1c1c1e",
                      border: `1px solid ${isInt ? "rgba(191,90,242,0.2)" : isOwn ? "rgba(0,212,255,0.2)" : "#2c2c2e"}`,
                      borderRadius: isOwn ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                      padding: "10px 14px",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6, alignItems: "center" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: isInt ? "#bf5af2" : isOwn ? "#00d4ff" : "#86868b" }}>
                          {c.author}{isInt && " · Internal"}
                        </span>
                        <span style={{ fontSize: 10, color: "#86868b" }}>{ago(c.created_at)}</span>
                      </div>
                      <div style={{ fontSize: 13, color: "#f5f5f7", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                        {c.body}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Comment input */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid #2c2c2e", background: "#111" }}>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) postComment(); }}
            placeholder={isStaff ? "Reply… (Ctrl+Enter to send)" : "Add a message… (Ctrl+Enter to send)"}
            rows={3}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#1c1c1e", border: "1px solid #3a3a3c",
              borderRadius: 10, padding: "10px 12px",
              color: "#f5f5f7", fontSize: 13, resize: "none",
              fontFamily: "inherit", outline: "none", marginBottom: 10,
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {isStaff ? (
              <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={internal}
                  onChange={e => setInternal(e.target.checked)}
                  style={{ accentColor: "#bf5af2" }}
                />
                <span style={{ fontSize: 12, color: internal ? "#bf5af2" : "#86868b" }}>
                  Internal note (hidden from client)
                </span>
              </label>
            ) : <span />}
            <button className="btn btn-primary" style={{ opacity: (posting || !comment.trim()) ? 0.5 : 1 }}
              onClick={postComment}
              disabled={posting || !comment.trim()}
             
            >
              {posting ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Assignee prompt (board moves out of Unassigned) ───────────────────────────

function AssignPrompt({ prompt, staff, onChange, onCancel, onConfirm }) {
  const t = prompt.ticket;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, backdropFilter: "blur(4px)",
    }} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="surface-dark" style={{
        background: "#1c1c1e", borderRadius: 16, width: "100%", maxWidth: 440,
        border: "1px solid #2c2c2e", overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
      }}>
        <div style={{ padding: "20px 24px 0" }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f5f5f7" }}>Assign {t.ref}</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "#86868b", lineHeight: 1.5 }}>
            Choose who picks this up before it moves to {prompt.toTitle}.
          </p>
        </div>
        <div style={{ padding: "16px 24px" }}>
          <div style={{ fontSize: 13, color: "#d1d1d6", marginBottom: 12, overflowWrap: "anywhere" }}>{t.title}</div>
          <label style={labelStyle}>Assign to</label>
          <select autoFocus value={prompt.assignee} onChange={e => onChange(e.target.value)} style={inputStyle}>
            <option value="">— select —</option>
            {staff.map(s => (
              <option key={s.username} value={s.username}>
                {s.email} {s.role === "manager" ? "(manager)" : "(admin)"}
              </option>
            ))}
          </select>
        </div>
        <div style={{ padding: "14px 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #2c2c2e" }}>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" style={{ opacity: prompt.assignee ? 1 : 0.5 }} onClick={onConfirm} disabled={!prompt.assignee}>
            Assign and move
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Tickets({ user, hideHeader = false }) {
  const isMobile = useIsMobile();
  const isCompact = useIsMobile(1440); // laptop-width tier — tighter columns, not the mobile card view
  const role       = user?.role || "client";
  const userSlug   = user?.client_slug || "";
  const currentUser = user?.username || "";
  const isStaff    = role === "admin" || role === "manager";

  const [tickets, setTickets]     = useState([]);
  const [stats, setStats]         = useState(null);
  const [clients, setClients]     = useState([]);
  const [staff, setStaff]         = useState([]);
  const [people, setPeople]       = useState([]);   // v1.12: Raised by suggestions (admins only; /api/users)
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editTicket, setEditTicket] = useState(null);
  const [selected, setSelected]   = useState(null);
  const [filters, setFilters]     = useState({
    status: "", type: "", priority: "", client_slug: "", assigned_to: "", q: ""
  });
  const [debouncedQ, setDebouncedQ] = useState("");
  const qTimer = useRef(null);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("stg_tickets_view") || "list"; } catch { return "list"; }
  });
  const [showClosedCol, setShowClosedCol] = useState(false);
  const [assignPrompt, setAssignPrompt] = useState(null); // { ticket, toStatus, assignee }
  const [boardError, setBoardError] = useState("");
  const isBoard = isStaff && view === "board";

  function changeView(v) {
    setView(v);
    try { localStorage.setItem("stg_tickets_view", v); } catch {}
  }

  // Debounce search
  useEffect(() => {
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => setDebouncedQ(filters.q), 300);
  }, [filters.q]);

  const loadTickets = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.status)      params.set("status", filters.status);
    if (filters.type)        params.set("type", filters.type);
    if (filters.priority)    params.set("priority", filters.priority);
    if (filters.client_slug) params.set("client_slug", filters.client_slug);
    if (filters.assigned_to) params.set("assigned_to", filters.assigned_to);
    if (debouncedQ)          params.set("q", debouncedQ);
    try {
      const res = await fetch(`${API}/api/tickets?${params}`, { credentials: "include" });
      if (res.ok) setTickets(await res.json());
    } catch {}
  }, [filters.status, filters.type, filters.priority, filters.client_slug, filters.assigned_to, debouncedQ]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tickets/stats`, { credentials: "include" });
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  const loadClients = useCallback(async () => {
    if (!isStaff) return;
    try {
      const res = await fetch(`${API}/api/clients`, { credentials: "include" });
      if (res.ok) setClients(await res.json());
    } catch {}
  }, [isStaff]);

  const loadStaff = useCallback(async () => {
    if (!isStaff) return;
    try {
      const res = await fetch(`${API}/api/staff`, { credentials: "include" });
      if (res.ok) setStaff(await res.json());
    } catch {}
  }, [isStaff]);

  const loadPeople = useCallback(async () => {
    if (role !== "admin") return;
    try {
      const res = await fetch(`${API}/api/users`, { credentials: "include" });
      if (res.ok) setPeople(await res.json());
    } catch {}
  }, [role]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadTickets(), loadStats(), loadClients(), loadStaff(), loadPeople()])
      .finally(() => setLoading(false));
  }, [loadTickets, loadStats, loadClients, loadStaff, loadPeople]);

  // Poll for updates every 30s
  useEffect(() => {
    const id = setInterval(() => { loadTickets(); loadStats(); }, 30000);
    return () => clearInterval(id);
  }, [loadTickets, loadStats]);

  function handleTicketSaved(t) {
    setTickets(prev => {
      const idx = prev.findIndex(x => x.id === t.id);
      if (idx >= 0) {
        const next = [...prev]; next[idx] = t; return next;
      }
      return [t, ...prev];
    });
    loadStats();
    setShowCreate(false);
    setEditTicket(null);
  }

  const handleUpdate = useCallback((t) => {
    setTickets(prev => prev.map(x => x.id === t.id ? t : x));
    setSelected(prev => (prev && prev.id === t.id) ? t : prev);
    loadStats();
  }, [loadStats]);

  const handleDeleteTicket = useCallback((ticketId) => {
    setTickets(prev => prev.filter(x => x.id !== ticketId));
    loadStats();
  }, [loadStats]);

  const handleRestore = useCallback((t) => {
    setTickets(prev => prev.some(x => x.id === t.id) ? prev : [t, ...prev]);
    loadStats();
  }, [loadStats]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v === f[k] ? "" : v }));

  // ── Board ──────────────────────────────────────────────────────────────────
  const BOARD_COLUMNS = [
    { id: "unassigned",  title: "Unassigned",  status: "Open",        color: "#86868b" },
    { id: "open",        title: "Open",        status: "Open",        color: STATUS_META["Open"].color },
    { id: "in_progress", title: "In Progress", status: "In Progress", color: STATUS_META["In Progress"].color },
    { id: "on_hold",     title: "On Hold",     status: "On Hold",     color: STATUS_META["On Hold"].color },
    { id: "resolved",    title: "Resolved",    status: "Resolved",    color: STATUS_META["Resolved"].color },
    { id: "closed",      title: "Closed",      status: "Closed",      color: STATUS_META["Closed"].color },
  ];

  function boardColumnFor(t) {
    if (t.status === "Open") return t.assigned_to ? "open" : "unassigned";
    const col = BOARD_COLUMNS.find(c => c.status === t.status && c.id !== "unassigned");
    return col ? col.id : "open";
  }

  const boardColumns = BOARD_COLUMNS
    .filter(c => showClosedCol || c.id !== "closed")
    .map(c => ({ ...c, items: tickets.filter(t => boardColumnFor(t) === c.id) }));

  async function putTicket(t, payload) {
    setBoardError("");
    const before = t;
    setTickets(prev => prev.map(x => x.id === t.id ? { ...x, ...payload } : x));
    try {
      const headers = { "Content-Type": "application/json" };
      const csrfStored = localStorage.getItem('stg_csrf');
      if (csrfStored) headers["X-CSRF-Token"] = csrfStored;
      const res = await fetch(`${API}/api/tickets/${t.id}`, { method: "PUT", headers, credentials: "include", body: JSON.stringify(payload) });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Update failed");
      }
      handleUpdate(await res.json());
    } catch (e) {
      setTickets(prev => prev.map(x => x.id === t.id ? before : x));
      setBoardError(`${t.ref}: ${e.message}`);
    }
  }

  function handleBoardMove(t, fromId, toId) {
    const target = BOARD_COLUMNS.find(c => c.id === toId);
    if (!target) return;
    if (toId === "unassigned") {
      putTicket(t, { status: "Open", assigned_to: null });
      return;
    }
    if (!t.assigned_to) {
      const me = staff.find(s => s.username === currentUser);
      setAssignPrompt({ ticket: t, toStatus: target.status, toTitle: target.title, assignee: me ? me.username : "" });
      return;
    }
    putTicket(t, { status: target.status });
  }

  function renderBoardCard(t) {
    const breached = t.sla_breach_at && !["Resolved", "Closed"].includes(t.status) && new Date(t.sla_breach_at) < new Date();
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
          <span style={{ fontFamily: "'SF Mono',monospace", fontSize: 11, color: "#00d4ff", fontWeight: 600 }}>{t.ref}</span>
          <span style={{ fontSize: 10, color: "#86868b", flexShrink: 0 }}>{ago(t.updated_at)}</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#f5f5f7", lineHeight: 1.3 }}>{t.title}</div>
        <div style={{ fontSize: 11, color: "#86868b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.client_name}</div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
          <Badge label={t.priority} meta={PRIORITY_META[t.priority]} />
          <span style={{ fontSize: 11, color: "#86868b" }}>{t.type}</span>{t.category && <CategoryTag c={t.category} />}
          {breached && <Badge label="SLA" meta={{ color: "#ff2d55", bg: "rgba(255,45,85,0.12)" }} />}
        </div>
        <div style={{ fontSize: 11, color: t.assigned_to ? "#d1d1d6" : "#636366" }}>
          {t.assigned_to ? t.assigned_to.split("@")[0] : "Unassigned"}
        </div>
      </div>
    );
  }

  // Priority breakdown for mini bars
  const totalOpen = stats?.open || 0;

  return (
    <div style={{ padding: isMobile ? "16px 12px" : isCompact ? "18px 20px" : "24px 28px", maxWidth: 1300, margin: "0 auto", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* Page header */}
      {!hideHeader && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h1 className="page-title" style={{ margin: "0 0 4px" }}>Tickets</h1>
            <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13 }}>
              {isStaff ? "Incidents, requests and changes across all clients" : "Your support tickets"}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {role === "admin" && (
              <button className="btn btn-secondary" onClick={() => setShowDeleted(true)}>Recently Deleted</button>
            )}
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              + New Ticket
            </button>
          </div>
        </div>
      )}
      {hideHeader && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 24 }}>
          {role === "admin" && (
            <button className="btn btn-secondary" onClick={() => setShowDeleted(true)}>Recently Deleted</button>
          )}
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New Ticket
          </button>
        </div>
      )}

      {/* Stats row */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 24 }}>
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Open" value={stats.open} color="#ff9f0a" />
          <StatCard label="In Progress" value={stats.in_progress} color="#00d4ff" />
          <StatCard label="Critical Open" value={stats.critical_open} color="#ff2d55" />
          {isStaff && <StatCard label="Unassigned" value={stats.unassigned_open} color="#86868b" sub="needs assignment" />}
          {isStaff && stats.sla_breached > 0 && (
            <StatCard label="SLA Breached" value={stats.sla_breached} color="#ff2d55" sub="past response time" />
          )}
        </div>
      )}

      {/* Filter chips — priority row, then status row */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: "#86868b", textTransform: "uppercase", letterSpacing: "0.08em" }}>Priority</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["Critical","High","Medium","Low"].map(p => (
              <button className="btn btn-secondary btn-sm" aria-pressed={filters.priority === p}
                key={p}
                onClick={() => setFilter("priority", p)}
              >{p}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: "#86868b", textTransform: "uppercase", letterSpacing: "0.08em" }}>Status</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["Open","In Progress","On Hold","Resolved","Closed"].map(s => (
            <button className="btn btn-secondary btn-sm" aria-pressed={filters.status === s}
              key={s}
              onClick={() => setFilter("status", s)}
            >{s}</button>
          ))}
          {isStaff && (
            <button className="btn btn-secondary btn-sm" aria-pressed={filters.assigned_to === "unassigned"}
              onClick={() => setFilter("assigned_to", "unassigned")}
            >Unassigned</button>
          )}
            {Object.values(filters).some(Boolean) && (
              <button className="btn btn-secondary btn-sm"
                onClick={() => setFilters({ status: "", type: "", priority: "", client_slug: "", assigned_to: "", q: "" })}
               
              >Clear filters</button>
            )}
          </div>
        </div>
      </div>

      {/* Search + type + client filter row */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {isStaff && (
          <div className="segmented" style={{ flexShrink: 0 }}>
            {[["list", "ti-list", "List"], ["board", "ti-layout-kanban", "Board"]].map(([v, icon, label]) => (
              <button key={v} className="btn" aria-pressed={view === v} onClick={() => changeView(v)}><i className={`ti ${icon}`} />{label}</button>
            ))}
          </div>
        )}
        <input
          value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          placeholder="Search tickets…"
          style={{ ...inputStyle, flex: "1 1 200px", minWidth: 180, margin: 0 }}
        />
        <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} style={{ ...inputStyle, margin: 0, width: "auto" }}>
          <option value="">All types</option>
          {["Incident","Request","Change"].map(t => <option key={t}>{t}</option>)}
        </select>
        {isStaff && (
          <select value={filters.client_slug} onChange={e => setFilters(f => ({ ...f, client_slug: e.target.value }))} style={{ ...inputStyle, margin: 0, width: "auto" }}>
            <option value="">All clients</option>
            {clients.map(c => <option key={c.slug} value={c.slug}>{c.display_name}</option>)}
          </select>
        )}
        {isStaff && (
          <select value={filters.assigned_to} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))} style={{ ...inputStyle, margin: 0, width: "auto" }}>
            <option value="">All assignees</option>
            <option value="unassigned">Unassigned</option>
            {staff.map(s => <option key={s.username} value={s.username}>{s.email}</option>)}
          </select>
        )}
        {isBoard && (
          <button className="btn btn-secondary btn-sm" aria-pressed={!!showClosedCol} onClick={() => setShowClosedCol(v => !v)}>{showClosedCol ? "Hide Closed" : "Show Closed"}</button>
        )}
      </div>

      {isBoard && boardError && (
        <div style={{ background: "rgba(255,45,85,0.1)", border: "1px solid rgba(255,45,85,0.3)", borderRadius: 8, padding: "10px 14px", color: "#ff2d55", fontSize: 13, marginBottom: 12 }}>
          {boardError}
        </div>
      )}

      {/* Ticket list */}
      {loading ? (
        <div style={{ color: "#86868b", textAlign: "center", padding: 60, fontSize: 14 }}>Loading tickets…</div>
      ) : isBoard ? (
        <Board
          columns={boardColumns}
          getId={t => t.id}
          renderCard={renderBoardCard}
          onMove={handleBoardMove}
          onOpen={t => setSelected(t)}
          isMobile={isMobile}
          emptyText="No tickets"
        />
      ) : tickets.length === 0 ? (
        <div className="surface-dark" style={{
          textAlign: "center", padding: "60px 20px",
          background: "#1c1c1e", borderRadius: 16, border: "1px solid #2c2c2e",
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}></div>
          <div style={{ color: "#f5f5f7", fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No tickets found</div>
          <div style={{ color: "#86868b", fontSize: 13 }}>
            {Object.values(filters).some(Boolean) ? "Try adjusting your filters" : "Create your first ticket to get started"}
          </div>
        </div>
      ) : isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tickets.map(t => (
            <div className="surface-dark"
              key={t.id}
              onClick={() => setSelected(t)}
              style={{
                padding: "12px 14px",
                background: "#1c1c1e",
                border: "1px solid #2c2c2e",
                borderRadius: 10,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                <span style={{ fontFamily: "'SF Mono',monospace", fontSize: 11, color: "#00d4ff", fontWeight: 600, flexShrink: 0 }}>
                  {t.ref}
                </span>
                <span style={{ fontSize: 11, color: "#86868b", flexShrink: 0 }}>{ago(t.updated_at)}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#f5f5f7", marginBottom: 6, overflowWrap: "break-word" }}>
                {t.title}
                {t.source === "email" && (
                  <span style={{ fontSize: 10, color: "#86868b", fontWeight: 400 }}> via email</span>
                )}
              </div>
              {isStaff && (
                <div style={{ fontSize: 12, color: "#86868b", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.client_name}
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#86868b" }}>{TYPE_ICONS[t.type]} {t.type}</span>{t.category && <CategoryTag c={t.category} />}
                <Badge label={t.priority} meta={PRIORITY_META[t.priority]} />
                <Badge label={t.status} meta={STATUS_META[t.status]} />
                {isStaff && (
                  <span style={{ fontSize: 11, color: t.assigned_to ? "#d1d1d6" : "#3a3a3c" }}>
                    {t.assigned_to ? t.assigned_to.split("@")[0] : "Unassigned"}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Table header */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isStaff
              ? (isCompact ? "72px 1fr 96px 76px 80px 88px 92px 76px" : "90px 1fr 120px 100px 90px 100px 110px 90px")
              : (isCompact ? "72px 1fr 96px 80px 88px 76px"          : "90px 1fr 120px 100px 90px 110px"),
            gap: 0, padding: isCompact ? "8px 12px" : "8px 16px",
            color: "#86868b", fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
          }}>
            <span>Ref</span>
            <span>Title</span>
            {isStaff && <span>Client</span>}
            <span>Type</span>
            <span>Priority</span>
            <span>Status</span>
            {isStaff && <span>Assigned</span>}
            <span>Updated</span>
          </div>

          {tickets.map((t, i) => (
            <div
              key={t.id}
              onClick={() => setSelected(t)}
              style={{
                display: "grid",
                gridTemplateColumns: isStaff
                  ? (isCompact ? "72px 1fr 96px 76px 80px 88px 92px 76px" : "90px 1fr 120px 100px 90px 100px 110px 90px")
                  : (isCompact ? "72px 1fr 96px 80px 88px 76px"          : "90px 1fr 120px 100px 90px 110px"),
                gap: 0, padding: isCompact ? "11px 12px" : "13px 16px",
                background: i % 2 === 0 ? "#161616" : "#1c1c1e",
                border: "1px solid #2c2c2e",
                borderRadius: i === 0 ? "10px 10px 0 0" : i === tickets.length - 1 ? "0 0 10px 10px" : 0,
                cursor: "pointer", transition: "background 0.12s",
                alignItems: "center",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#222"}
              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#161616" : "#1c1c1e"}
            >
              <span style={{ fontFamily: "'SF Mono',monospace", fontSize: 11, color: "#00d4ff", fontWeight: 600 }}>
                {t.ref}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#f5f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.title}
                </div>
                {t.source === "email" && (
                  <span style={{ fontSize: 10, color: "#86868b" }}> via email</span>
                )}
              </div>
              {isStaff && (
                <span style={{ fontSize: 12, color: "#86868b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.client_name}
                </span>
              )}
              <span style={{ fontSize: 12, color: "#86868b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{TYPE_ICONS[t.type]} {t.type}</span>{t.category && <CategoryTag c={t.category} />}
              <Badge label={t.priority} meta={PRIORITY_META[t.priority]} />
              <Badge label={t.status} meta={STATUS_META[t.status]} />
              {isStaff && (
                <span style={{ fontSize: 11, color: t.assigned_to ? "#d1d1d6" : "#3a3a3c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.assigned_to ? t.assigned_to.split("@")[0] : "—"}
                </span>
              )}
              <span style={{ fontSize: 11, color: "#86868b" }}>{ago(t.updated_at)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Modals & drawer */}
      {(showCreate || editTicket) && (
        <TicketModal
          onClose={() => { setShowCreate(false); setEditTicket(null); }}
          onSave={handleTicketSaved}
          clients={clients}
          staff={staff}
          role={role}
          userSlug={userSlug}
          editTicket={editTicket}
          people={people}
          currentUser={currentUser}
        />
      )}

      {showDeleted && (
        <DeletedTicketsPanel
          onClose={() => setShowDeleted(false)}
          onRestore={handleRestore}
        />
      )}

      {assignPrompt && (
        <AssignPrompt
          prompt={assignPrompt}
          staff={staff}
          onChange={assignee => setAssignPrompt(p => ({ ...p, assignee }))}
          onCancel={() => setAssignPrompt(null)}
          onConfirm={() => {
            const p = assignPrompt;
            setAssignPrompt(null);
            putTicket(p.ticket, { status: p.toStatus, assigned_to: p.assignee });
          }}
        />
      )}

      {selected && (
        <TicketDrawer
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
          onDelete={handleDeleteTicket}
          staff={staff}
          role={role}
          currentUser={currentUser}
          people={people}
        />
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: "#86868b", letterSpacing: "0.06em",
  textTransform: "uppercase", marginBottom: 5,
};

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: "#2c2c2e", border: "1px solid #3a3a3c",
  borderRadius: 8, padding: "9px 12px",
  color: "#f5f5f7", fontSize: 13, outline: "none",
  fontFamily: "inherit",
};




const inlineSelect = {
  background: "#2c2c2e", border: "1px solid #3a3a3c",
  borderRadius: 7, padding: "6px 10px", color: "#f5f5f7",
  fontSize: 12, fontWeight: 600, outline: "none", cursor: "pointer",
  fontFamily: "inherit",
};